/**
 * Invoice API Endpoints
 * API calls for invoice operations
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import type { InvoicesResponse, InvoicesQueryParams } from '../types/invoice.types';
import type { InvoiceDetails } from '@/types/invoice';

const COMMERCE_SERVICE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL ||
  'http://localhost:8083/commerce-service';

function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Fetch invoices list with filters
 */
export async function getInvoices(params: InvoicesQueryParams): Promise<InvoicesResponse> {
  const queryParams = new URLSearchParams();

  // Pagination
  queryParams.set('page', String(params.page));
  queryParams.set('size', String(params.size));

  // Search
  if (params.searchTerm) {
    queryParams.set('search', params.searchTerm);
  }

  // Filters
  if (params.statuses?.length) {
    params.statuses.forEach(status => {
      queryParams.append('statuses', status);
    });
  }

  // Date range
  if (params.dateFrom) {
    queryParams.set('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.set('dateTo', params.dateTo);
  }

  // Sorting
  if (params.sortBy) {
    queryParams.set('sortBy', params.sortBy);
  }
  if (params.sortDirection) {
    queryParams.set('sortDirection', params.sortDirection);
  }

  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch invoices');
  }

  const data = await response.json();
  return data;
}

/**
 * Fetch single invoice details
 */
export async function getInvoiceDetails(invoiceId: number): Promise<InvoiceDetails> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch invoice details');
  }

  const data = await response.json();
  return data;
}

/**
 * Export invoices as CSV
 */
export async function exportInvoicesCSV(params: InvoicesQueryParams): Promise<Blob> {
  const queryParams = new URLSearchParams();

  // Add filters
  if (params.searchTerm) {
    queryParams.set('search', params.searchTerm);
  }
  if (params.statuses?.length) {
    params.statuses.forEach(status => {
      queryParams.append('statuses', status);
    });
  }
  if (params.dateFrom) {
    queryParams.set('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.set('dateTo', params.dateTo);
  }

  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/export/csv?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export invoices');
  }

  return response.blob();
}

/**
 * Export invoice as PDF
 */
export async function exportInvoicePDF(invoiceId: number): Promise<Blob> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}/export/pdf`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export invoice as PDF');
  }

  return response.blob();
}

/**
 * Send invoice email
 */
export async function sendInvoiceEmail(
  invoiceId: number,
  email: string,
  ccEmails?: string[]
): Promise<{ success: boolean; message: string }> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}/send-email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        recipientEmail: email,
        ccEmails: ccEmails || [],
        includeAttachment: true,
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to send invoice email');
  }

  const data = await response.json();
  return data;
}

export const invoiceApi = {
  getInvoices,
  getInvoiceDetails,
  exportInvoicesCSV,
  exportInvoicePDF,
  sendInvoiceEmail,
};

export default invoiceApi;
